import React, { useState, useMemo } from 'react'
import { Button, Form, Table } from 'react-bootstrap'

const COLUMNS = [
  { key: 'Symbol', label: 'Symbol' },
  { key: 'Security', label: 'Name' },
  { key: 'GICS Sector', label: 'Sector' },
  { key: 'GICS Sub-Industry', label: 'Sub-Industry' },
  { key: 'Headquarters Location', label: 'Headquarters' },
  { key: 'Date added', label: 'Date Added' },
  { key: 'CIK', label: 'CIK' },
  { key: 'Founded', label: 'Founded' },
]

const PAGE_SIZE = 25

function Snp500Table({ items }) {
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState(null)
  const [sortDir, setSortDir] = useState('asc')
  const [page, setPage] = useState(1)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return items
    return items.filter(
      (item) =>
        (item.Symbol || '').toLowerCase().includes(q) ||
        (item.Security || '').toLowerCase().includes(q)
    )
  }, [items, search])

  const sorted = useMemo(() => {
    if (!sortKey) return filtered
    return [...filtered].sort((a, b) => {
      const cmp = (a[sortKey] || '').localeCompare(b[sortKey] || '')
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [filtered, sortKey, sortDir])

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const pageItems = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
    setPage(1)
  }

  function handleSearch(e) {
    setSearch(e.target.value)
    setPage(1)
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-5 text-muted">
        <p className="mb-0">No data loaded. Run an update to load S&amp;P 500 data.</p>
      </div>
    )
  }

  return (
    <>
      <Form.Control
        type="text"
        placeholder="Search by Symbol or Name…"
        value={search}
        onChange={handleSearch}
        className="mb-3"
        aria-label="Search"
      />
      <Table responsive hover size="sm">
        <thead>
          <tr>
            {COLUMNS.map(({ key, label }) => (
              <th key={key} scope="col">
                <button
                  type="button"
                  className="btn btn-link p-0 text-decoration-none text-reset fw-semibold"
                  onClick={() => handleSort(key)}
                >
                  {label}
                  {sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pageItems.map((item, i) => (
            <tr key={item.Symbol || i}>
              {COLUMNS.map(({ key }) => (
                <td key={key}>{item[key]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
      {totalPages > 1 && (
        <div className="d-flex align-items-center gap-3 mt-2">
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={currentPage === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="text-muted small">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={currentPage === totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </>
  )
}

export default Snp500Table
